/**
 * cot-task-derive
 *
 * Pure normalization of an agent's task-list events into an ordered task model
 * for the conversation task list (rendered below the CoT). No React, no
 * subscriptions — a pure fold over a `PlatformEvent[]`, mirroring
 * `cot-derive.ts` and the reducer helpers in `AuiAgentTaskList.tsx`.
 *
 * Two backend vocabularies are normalized into one model:
 *
 *  1. `task.*`                    — `task.started` / `task.progress` / `task.completed`
 *  2. `agent.run.agent_task_*`    — the existing production contract (AgentTaskEventData)
 *
 * Unknown event types are ignored, so this can be called on a mixed stream
 * (e.g. task events interleaved with `agent.run.reasoning_*` / `tool_call`).
 */

import type { PlatformEvent } from '../../types/events';
import type { AgentTaskStatus, AgentTaskEventData } from '../../types/agent-tasks';

/** Status vocabulary for a conversation task — reuses the production union. */
export type ConversationTaskStatus = AgentTaskStatus;

/** A single task in the conversation task list. */
export interface ConversationTask {
  taskId: string;
  title: string;
  status: ConversationTaskStatus;
  /** The current step / activeForm shown while the task is running. */
  activeForm?: string;
}

/** Shape of the literal `task.*` event payloads (from the raw agent log). */
interface RawTaskEventData {
  taskId?: string;
  description?: string; // task.started — the task title
  currentStep?: string; // task.progress — the active step
  summary?: string; // task.completed — result summary
}

/** Shape of an `agent.run.tool_call` payload we care about (TaskCreate). */
interface ToolCallData {
  toolName?: string;
  input?: { subject?: string };
}

function getData(event: PlatformEvent): Record<string, unknown> {
  return (event.data as Record<string, unknown> | undefined) ?? {};
}

/** Ordered task map — insertion order is display order (creation order). */
export type ConversationTaskMap = Map<string, ConversationTask>;

function upsert(
  tasks: ConversationTaskMap,
  taskId: string,
  patch: Partial<ConversationTask> & { title?: string },
) {
  const existing = tasks.get(taskId);
  if (existing) {
    tasks.set(taskId, {
      ...existing,
      ...patch,
      // Never blank out a known title with an empty patch title.
      title: patch.title || existing.title,
    });
  } else {
    tasks.set(taskId, {
      taskId,
      title: patch.title ?? taskId,
      status: patch.status ?? 'pending',
      activeForm: patch.activeForm,
    });
  }
}

/**
 * Applies a single task-list event to the ordered map, in place. Shared by the
 * batch {@link deriveConversationTasks} (dev-page playback) and the live
 * subscription hook in the conversation. Unknown event types are ignored, so
 * this is safe to call on a mixed stream (task events interleaved with
 * reasoning / tool-call events).
 */
export function applyTaskEvent(tasks: ConversationTaskMap, event: PlatformEvent): void {
  const type = event.eventType;

  // ── Literal task.* vocabulary ────────────────────────────────────────────
  if (type === 'task.started' || type === 'task.progress' || type === 'task.completed') {
    const data = getData(event) as RawTaskEventData;
    if (!data.taskId) return;

    if (type === 'task.started') {
      // In the raw log, `task.started` fires at creation — it means *open*.
      upsert(tasks, data.taskId, { title: data.description, status: 'pending' });
    } else if (type === 'task.progress') {
      upsert(tasks, data.taskId, { status: 'running', activeForm: data.currentStep });
    } else {
      // task.completed
      upsert(tasks, data.taskId, { status: 'completed', activeForm: undefined });
    }
    return;
  }

  // ── TaskCreate tool call — title source when task.started lacks one ───────
  if (type === 'agent.run.tool_call') {
    const data = getData(event) as ToolCallData;
    if (data.toolName === 'TaskCreate' && data.input?.subject) {
      // Tool calls carry no taskId; we can't key a task here. Skip — the
      // task.started / agent_task_created events carry the title we need.
      // (Kept as an explicit branch so the intent is clear.)
    }
    return;
  }

  // ── Existing agent.run.agent_task_* vocabulary ───────────────────────────
  if (type.startsWith('agent.run.agent_task_')) {
    const data = getData(event) as unknown as AgentTaskEventData;
    if (!data.taskId) return;
    const suffix = type.replace('agent.run.agent_task_', '');

    switch (suffix) {
      case 'deleted':
        tasks.delete(data.taskId);
        break;
      case 'created':
        upsert(tasks, data.taskId, {
          title: data.title,
          status: data.status ?? 'pending',
          activeForm: data.activeForm,
        });
        break;
      case 'started':
        upsert(tasks, data.taskId, { status: 'running', activeForm: data.activeForm });
        break;
      case 'completed':
        upsert(tasks, data.taskId, { status: 'completed', activeForm: undefined });
        break;
      case 'failed':
        upsert(tasks, data.taskId, { status: 'failed', activeForm: undefined });
        break;
      case 'blocked':
        upsert(tasks, data.taskId, { title: data.title, status: 'blocked' });
        break;
      case 'unblocked':
        upsert(tasks, data.taskId, { status: 'running' });
        break;
      default:
        // Unknown suffix — apply any provided status, else leave as-is.
        if (data.status) upsert(tasks, data.taskId, { status: data.status });
    }
    return;
  }

  // Everything else (reasoning, response, etc.) is ignored.
}

/**
 * Folds task-list events into an ordered `ConversationTask[]`.
 *
 * Order is the creation order of each task (first time its id is seen). Later
 * events for a task mutate it in place, preserving that original position.
 */
export function deriveConversationTasks(events: PlatformEvent[]): ConversationTask[] {
  const tasks: ConversationTaskMap = new Map();
  for (const event of events) applyTaskEvent(tasks, event);
  return [...tasks.values()];
}
